import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DateTimeHeader from './DateTimeHeader';
import {
  Bell,
  Calendar as CalendarIcon,
  RefreshCw,
  CalendarCheck,
  MessageCircle,
  AlertCircle,
  Moon,
  Sun,
  X,
  UserPlus,
  Clock,
  Trash2,
  LogOut
} from "lucide-react";
import Dropdown from './Dropdown';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';

import { NotificationStatus, NotificationType, Notification } from '../types/user';
// FCM + browser
import {
  initializeForegroundNotifications,
  requestNotificationPermission
} from '../services/notificationService';

// Backend (Redis)
import {
  fetchUserNotifications,
  dismissRedisNotification,
  clearAllRedisNotifications,
  restoreRedisNotification,
  restoreAllRedisNotifications
} from '../utils/notificationManager';


import { useMeeting } from '../contexts/MeetingContext';






const NavBar: React.FC = () => {

  const [time, setTime] = useState<string>("");
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  // console.log("notifications in NavBar:", notifications);

  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const { fetchAllMeetings } = useMeeting();
  const [notificationsTab, setNotificationsTab] = useState<"current" | "cleared">("current");
  const [clearedNotifications, setClearedNotifications] = useState<Notification[]>([]);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const handleRestoreNotification = async (id: string | number) => {
    const notificationId = String(id);

    const success = await restoreRedisNotification(notificationId);

    if (success) {
      const restored = clearedNotifications.find((n) => n.id === id);

      // 1. Move back to Current tab list
      if (restored) {
        setNotifications((prev) => [restored, ...prev]);
      }

      // 2. Remove from Cleared list
      setClearedNotifications((prev) => prev.filter((n) => n.id !== id));
    } else {
      console.error(`Failed to restore notification ${notificationId} on backend.`);
    }
  };

  const handleRestoreAll = async () => {
    const success = await restoreAllRedisNotifications();

    if (success) {
      // 1. Move all back to Current tab list
      setNotifications((prev) => [...prev, ...clearedNotifications]);

      // 2. Empty the Cleared list
      setClearedNotifications([]);
    } else {
      console.error("Failed to restore all notifications via backend.");
    }
  };

  const handleClearNotification = async (id: string | number) => {
    const notificationId = String(id);

    // NOTE: This calls PUT /:id/dismiss (mark as DISMISSED)
    const success = await dismissRedisNotification(notificationId);

    if (success) {
      const removed = notifications.find((n) => n.id === id);

      // 1. Move to Cleared tab list
      if (removed) {
        setClearedNotifications((prev) => [...prev, removed]);
      }

      // 2. Remove from Current list
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } else {
      console.error(`Failed to dismiss notification ${notificationId} on backend.`);
      // Handle error: show error message to user if necessary
    }
  };

  const handleClearAll = async () => {
    // NOTE: This calls the updated clearAllRedisNotifications()
    // which hits PUT /api/notifications/all/dismiss (Soft Clear All)
    const success = await clearAllRedisNotifications();

    if (success) {
      // 1. Move all to Cleared tab list
      setClearedNotifications((prev) => [...prev, ...notifications]);

      // 2. Empty the Current list
      setNotifications([]);
    } else {
      console.error("Failed to clear all notifications via backend.");
      // Handle error
    }
  };

  // ... (Rest of the file remains unchanged)
  const { user, logout } = useAuth();
  // console.log("user in NavBar11:", user);


  const handleLogout = () => {
    console.log("Logging out user:", user?.first_name + " " + user?.last_name);
    logout();           // clear user and token
    navigate("/login"); // redirect to login page
  };


  const getNotificationIcon = (notification: Notification) => {
    switch (notification.type) {
      case NotificationType.MEETING_REMINDER:
        return <Clock className="text-blue-400" size={18} />;
      case NotificationType.MEETING_INVITATION:
        return <UserPlus className="text-green-400" size={18} />;
      case NotificationType.MEETING_CANCELLED:
        return <X className="text-red-400" size={18} />;
      case NotificationType.MEETING_UPDATED:
        return <CalendarCheck className="text-yellow-400" size={18} />;
      default:
        return <Bell className="text-gray-400" size={18} />;
    }
  };

  const formatNotificationTime = (createdAt: string) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffInMinutes = Math.floor((now.getTime() - created.getTime()) / 60000);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };


  useEffect(() => {
    const update = () =>
      setTime(
        new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      );
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = initializeForegroundNotifications((notification: Notification) => {
      setNotifications(prev => {
        if (prev.some(n => n.id === notification.id)) return prev;
        return [notification, ...prev];
      });

      setUnreadCount(prev => prev + 1);
    });

    requestNotificationPermission();

    return () => {
      unsubscribe();
    };
  }, [user?.id]);


  useEffect(() => {
    fetchUserNotifications(user?.id || 0).then(fetchedNotifications => {
      setNotifications(fetchedNotifications);
      // console.log("Fetched notifications:", fetchedNotifications);

    });
  }, [user?.id]);

  const handleNotificationAction = async (action: string, notification: Notification) => {
    if (notification.type === NotificationType.MEETING_INVITATION) {
      try {
        const inviteToken = notification.data?.inviteToken;
        if (!inviteToken) {
          console.warn('Invite token missing on notification', notification.id);
        }

        if (action === 'accept' && inviteToken) {
          // Call backend to accept invite
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/meetings/accept-invite/${inviteToken}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });

          if (!res.ok) {
            console.error('Failed to accept invite:', res.status);
          }

          // reload meetings
          fetchAllMeetings();

          // remove from UI only
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
        }

        else if (action === 'decline') {
          // Optionally call decline endpoint if available, otherwise just dismiss
          await dismissRedisNotification(notification.id);
          setNotifications(prev => prev.filter(n => n.id !== notification.id));
        }
      } catch (error) {
        console.error('Error handling notification action:', error);
      }
    }
  };


  const renderNotification = (notification: Notification) => {
    // console.log("Rendering notification:", notification);

    return (
      <div
        key={notification.id}
        className={`p-4 rounded-lg border border-gray-200 dark:border-gray-700 ${notification.status === NotificationStatus.UNREAD
          ? 'bg-blue-50 dark:bg-blue-900/20'
          : 'bg-gray-50 dark:bg-gray-800/20'
          }`}
      >
        <div className="flex items-start gap-3">
          {getNotificationIcon(notification)}
          <div className="flex-1">
            <h4 className="text-sm font-medium">{notification.title}</h4>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {notification.message}
            </p>
            <span className="text-xs text-gray-500 dark:text-gray-500">
              {formatNotificationTime(notification.createdAt)}
            </span>
          </div>
          {notification.type === NotificationType.MEETING_INVITATION && (
            <div className="flex gap-2 flex-col">
              <button
                onClick={() => handleNotificationAction('accept', notification)}
                className="px-3 py-1 text-xs font-medium text-white bg-green-500 rounded-md"
              >
                Accept
              </button>
              <button
                onClick={() => handleNotificationAction('decline', notification)}
                className="px-3 py-1 text-xs font-medium text-white bg-red-500 rounded-md"
              >
                Decline
              </button>
            </div>
          )}
          {notification.type === NotificationType.MEETING_CANCELLED && (
            <div className="flex gap-2">
              <button
                onClick={() => dismissRedisNotification(notification.id).then(() => setNotifications(prev => prev.filter(n => n.id !== notification.id)))}
                className="px-3 py-1 text-xs font-medium text-white bg-gray-600 rounded-md"
              >
                Dismiss
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
      <div className="hidden md:flex items-center justify-between gap-4">
        <DateTimeHeader darkMode={darkMode} />
      </div>
      <div className="hidden md:flex items-center justify-between gap-6">
        <button
          onClick={() => window.location.reload()}
          className={`p-2 rounded-lg transition ${darkMode ? "hover:bg-white/10" : "hover:bg-gray-200"
            }`}
        >
          <RefreshCw
            className={`${darkMode ? "text-white/70" : "text-gray-600"}`}
            size={22}
          />
        </button>
        <Dropdown
          trigger={
            <button
              className={`relative p-2 rounded-lg transition-all duration-200 group ${darkMode ? "hover:bg-white/10" : "hover:bg-gray-200"
                }`}
            >
              <Bell
                className={`${darkMode
                  ? "text-white/70 group-hover:text-white"
                  : "text-gray-600 group-hover:text-gray-800"
                  }`}
                size={22}
              />
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-red-500 to-pink-500 rounded-full border-2 border-white"></span>
              )}
            </button>
          }
          align="right"
          darkMode={darkMode}
        >
          <div
            className={`md:w-96 w-15 rounded-xl shadow-2xl border p-4 ${darkMode
              ? "bg-[#232336] border-white/10"
              : "bg-white border-gray-200"
              }`}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pl-3">
              <div className="flex items-center gap-2">
                <h3
                  className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  Notifications
                </h3>
              </div>
              {notifications.length > 0 && (
                <span className="px-2 py-1 bg-green-600/20 text-green-600 text-xs rounded-full font-medium">
                  {notifications.length} new
                </span>
              )}
            </div>
            {/* Notifications List */}
            <div className="max-h-64 overflow-y-auto scrollbar-hide">
              {
                // loading ? (
                //   <div className="text-center py-8">
                //     <p className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"}`}>
                //       Loading notifications...
                //     </p>
                //   </div>
                // ) : 
                notifications.length === 0 ? (
                  <div className="text-center py-8">
                    <div
                      className={`${darkMode ? "bg-white/5" : "bg-gray-100"
                        } w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full`}
                    >
                      <Bell
                        size={20}
                        className={`${darkMode ? "text-white/40" : "text-gray-400"
                          }`}
                      />
                    </div>
                    <p
                      className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                    >
                      No notifications
                    </p>
                    <p
                      className={`text-xs mt-1 ${darkMode ? "text-white/40" : "text-gray-500"
                        }`}
                    >
                      You're all caught up!
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notifications.slice(0, 4).map((n) => (
                      renderNotification(n)
                    ))}
                  </div>
                )}
            </div>
            {/* Footer */}
            <div
              className={`mt-4 pt-3 border-t ${darkMode ? "border-white/10" : "border-gray-200"}`}
            >
              <button
                className="w-full text-center text-sm font-medium py-2 px-4 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 hover:scale-105 text-white transition-all duration-200 shadow-lg"
                onClick={() => setShowNotificationsModal(true)}
              >
                View all notifications
              </button>
            </div>

          </div>
        </Dropdown>
        {showNotificationsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-[#232336] rounded-2xl shadow-2xl p-8 md:p-12 w-[90vw] max-w-4xl relative max-h-[90vh] overflow-y-auto scrollbar-hide">

              {/* Close Button */}
              <button
                className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors duration-200"
                onClick={() => setShowNotificationsModal(false)}
              >
                <X size={28} />
              </button>

              {/* Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-red-600 to-pink-600">
                    <Bell size={20} className="text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-white">Notifications</h2>
                </div>
                <p className="text-white/60 text-lg">
                  Manage your notifications and stay updated
                </p>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 mb-8 bg-white/5 rounded-lg p-1 w-full">
                <button
                  className={` flex-1 px-6 py-3 rounded-md text-sm font-medium transition ${notificationsTab === "current"
                    ? "bg-white/10 text-white"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                    }`}
                  onClick={() => setNotificationsTab("current")}
                >
                  Current ({notifications.length})
                </button>

                <button
                  className={`flex-1 px-6 py-3 rounded-md text-sm font-medium transition ${notificationsTab === "cleared"
                    ? "bg-white/10 text-white"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                    }`}
                  onClick={() => setNotificationsTab("cleared")}
                >
                  Cleared ({clearedNotifications.length})
                </button>
              </div>

              {/* CURRENT TAB */}
              {notificationsTab === "current" && (
                <div className="space-y-6">
                  {notifications.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-white/5">
                        <Bell size={32} className="text-white/40" />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-2">
                        No notifications
                      </h3>
                      <p className="text-white/60">You're all caught up!</p>
                    </div>
                  ) : (
                    <>
                      <div className="grid gap-4">
                        {notifications.map((n) => (
                          <div
                            key={n.id}
                            className="group bg-white/5 rounded-xl p-6 border border-white/10 hover:border-white/20 transition-all duration-200"
                          >
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 mt-1">{getNotificationIcon(n)}</div>

                              <div className="flex-1 min-w-0">
                                <p className="text-white/90 text-base leading-relaxed mb-2">
                                  {n.title}                        </p>

                                <div className="flex items-center gap-4 text-white/40 text-sm">
                                  <span>{new Date(n.createdAt).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}</span>

                                  {/* <span>•</span>

                                  <span>Unread</span> */}
                                </div>

                              </div>

                              {/* Delete Icon EXACT like UI 1 */}
                              <button
                                className="flex-shrink-0 p-2 rounded-lg hover:bg-red-600/20 text-white/60 hover:text-red-400 transition-all duration-200 group-hover:opacity-100 opacity-0"
                                onClick={() => handleClearNotification(n.id)}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Footer */}
                      <div className="flex gap-4 pt-6 border-t border-white/10">
                        <button
                          className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl transition-all duration-200"
                          onClick={() => setShowNotificationsModal(false)}
                        >
                          Close
                        </button>

                        <button
                          className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:scale-105 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={handleClearAll}
                          disabled={notifications.length === 0}
                        >
                          Clear All Notifications
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* CLEARED TAB */}
              {notificationsTab === "cleared" && (
                <div className="space-y-6">
                  {clearedNotifications.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-white/5">
                        <Trash2 size={32} className="text-white/40" />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-2">
                        No cleared notifications
                      </h3>
                      <p className="text-white/60">
                        Notifications you clear will appear here.
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="grid gap-4">
                        {clearedNotifications.map((n) => (
                          <div
                            key={n.id}
                            className="group bg-white/5 rounded-xl p-6 border border-white/10 opacity-60 hover:opacity-100 transition-opacity duration-200"
                          >
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 mt-1">{getNotificationIcon(n)}</div>

                              <div className="flex-1 min-w-0">
                                <p className="text-white/70 text-base leading-relaxed mb-2 line-through">
                                  {n.title}
                                </p>

                                <div className="flex items-center gap-4 text-white/30 text-sm">
                                  <span>Cleared</span>
                                  <span>•</span>
                                  <span>
                                    {new Date(n.createdAt).toLocaleString("en-GB", {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })}
                                  </span>
                                </div>
                              </div>

                              {/* Restore Button */}
                              <button
                                className="flex-shrink-0 p-2 rounded-lg hover:bg-red-600/20 text-white/60 hover:text-red-400 transition-all duration-200 group-hover:opacity-100 opacity-0"
                                onClick={() => handleRestoreNotification(n.id)}
                                title="Restore notification"
                              >
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Footer */}
                      {/* <div className="flex gap-4 pt-6 border-t border-white/10">
          <button
            className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl transition-all duration-200"
            onClick={() => setShowNotificationsModal(false)}
          >
            Close
          </button>

          <button
            className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:scale-105 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleRestoreAll}
            disabled={clearedNotifications.length === 0}
          >
            Restore All Notifications
          </button>
        </div> */}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        <Dropdown
          trigger={
            <div
              className={`flex items-center gap-3 p-2 rounded-xl cursor-pointer transition-all duration-200 ${darkMode ? "hover:bg-white/10" : "hover:bg-gray-100"
                }`}
            >
              <img
                src={user?.profile_picture ? `${process.env.REACT_APP_BACKEND_URL}${user?.profile_picture}` : "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"}
                alt="profile"
                className="w-10 h-10 rounded-full border-2 border-white/20"
              />
              <div className="hidden md:block text-left">
                <div
                  className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                    }`}
                >
                  {user ? user.first_name + " " + user.last_name : "Guest User"}
                </div>
                {/* <div
                  className={`text-xs ${darkMode ? "text-white/60" : "text-gray-500"
                    }`}
                >
                  Online
                </div> */}
              </div>
              <svg
                className={`w-4 h-4 ${darkMode ? "text-white/40" : "text-gray-400"
                  }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          }
          align="right"
          darkMode={darkMode}
        >
          <div className="w-full max-w-xs sm:max-w-sm">
            {/* Header */}
            <div className="p-4 sm:p-6 pb-3 sm:pb-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="relative flex-shrink-0">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full p-0.5 bg-gradient-to-r from-pink-500 to-red-500">
                    <div className="w-full h-full rounded-full overflow-hidden">
                      <img
                        src={user?.profile_picture ? `${process.env.REACT_APP_BACKEND_URL}${user?.profile_picture}` : "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"}
                        alt="avatar"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3
                    className={`text-base sm:text-lg font-bold truncate ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    {user ? user.first_name + " " + user.last_name : "Guest User"}
                  </h3>
                  <p
                    className={`text-xs sm:text-sm truncate ${darkMode ? "text-white/60" : "text-gray-600"
                      }`}
                  >
                    {user ? user.email : "guest.user@email.com"}
                  </p>
                  <div
                    className={`text-xs sm:text-sm mt-1 ${darkMode ? "text-white/70" : "text-gray-600"
                      }`}
                  >
                    {user ? user.job : " "}
                  </div>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="px-2 sm:px-3 pb-3">
              <div className="space-y-0.5 sm:space-y-1">
                <button
                  onClick={() => navigate("/settings")}
                  className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-left transition-all duration-200 group ${darkMode ? "hover:bg-white/10" : "hover:bg-gray-50"
                    }`}
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <svg
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-xs sm:text-sm font-semibold truncate ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      Settings
                    </div>
                    <div
                      className={`text-xs truncate ${darkMode ? "text-white/50" : "text-gray-500"
                        }`}
                    >
                      Preferences & privacy
                    </div>
                  </div>
                  <svg
                    className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${darkMode ? "text-white/30" : "text-gray-400"
                      } group-hover:scale-110 transition-transform duration-200`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>

                <button
                  onClick={() => navigate("/help")}
                  className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-left transition-all duration-200 group ${darkMode ? "hover:bg-white/10" : "hover:bg-gray-50"
                    }`}
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <svg
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-xs sm:text-sm font-semibold truncate ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      Help & Support
                    </div>
                    <div
                      className={`text-xs truncate ${darkMode ? "text-white/50" : "text-gray-500"
                        }`}
                    >
                      Get assistance
                    </div>
                  </div>
                  <svg
                    className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${darkMode ? "text-white/30" : "text-gray-400"
                      } group-hover:scale-110 transition-transform duration-200`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
              <div
                className={`my-2 sm:my-3 mx-2 sm:mx-3 h-px ${darkMode
                  ? "bg-gradient-to-r from-transparent via-white/10 to-transparent"
                  : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"
                  }`}
              ></div>
              <button
                onClick={() => setShowLogoutModal(true)}
                className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-left transition-all duration-200 group ${darkMode
                  ? "hover:bg-red-500/10 hover:text-red-400"
                  : "hover:bg-red-50 text-red-600"
                  }`}
              >
                <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                  <svg
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs sm:text-sm font-semibold truncate">
                    <button>
                      {user ? "Sign Out" : "Login"}
                    </button>
                  </div>
                  <div
                    className={`text-xs truncate ${darkMode ? "text-white/50" : "text-gray-500"
                      }`}
                  >
                    Log out of your account
                  </div>
                </div>
                <svg
                  className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${darkMode ? "text-white/30" : "text-gray-400"
                    } group-hover:scale-110 transition-transform duration-200`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
            {showLogoutModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#232336] backdrop-blur-sm rounded-2xl">
                <div className="bg-[#232336] w-[90vw] max-w-md rounded-2xl p-8 shadow-2xl relative">

                  {/* Close Button */}
                  <button
                    className="absolute top-4 right-4 text-white/60 hover:text-white transition"
                    onClick={() => setShowLogoutModal(false)}
                  >
                    <X size={26} />
                  </button>

                  {/* Icon */}
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center 
      bg-gradient-to-br from-red-600 to-pink-600 shadow-lg">
                    <LogOut className="text-white" size={28} />
                  </div>

                  {/* Title */}
                  <h2 className="text-2xl font-bold text-white text-center mb-2">
                    Sign Out?
                  </h2>

                  {/* Description */}
                  <p className="text-center text-white/60 mb-6">
                    Are you sure you want to log out from your account?
                  </p>

                  {/* Buttons */}
                  <div className="flex gap-4">
                    <button
                      className="flex-1 py-3 rounded-xl bg-white/10 text-white border border-white/20 
          hover:bg-white/20 transition"
                      onClick={() => setShowLogoutModal(false)}
                    >
                      Cancel
                    </button>

                    <button
                      className="flex-1 py-3 rounded-xl bg-gradient-to-r from-red-600 to-pink-600 text-white
          font-semibold shadow-lg hover:scale-105 transition"
                      onClick={handleLogout}
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </Dropdown>

      </div>


      {/* ================= MOBILE LAYOUT ================= */}
      <div className="md:hidden flex flex-row gap-3 mt-5">

        {/* Date & Time (FULL ROW) */}
        <div className="w-1/2  ">
          <DateTimeHeader darkMode={darkMode} />
        </div>

        {/* Mobile Actions */}
        <div className="flex justify-end gap-4 w-1/2 mr-3">

          {/* 🔔 Notification */}
          <Dropdown
            trigger={
              <button
                className={`relative p-2 rounded-lg transition-all duration-200 group ${darkMode ? "hover:bg-white/10" : "hover:bg-gray-200"
                  }`}
              >
                <Bell
                  className={`${darkMode
                    ? "text-white/70 group-hover:text-white"
                    : "text-gray-600 group-hover:text-gray-800"
                    }`}
                  size={22}
                />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-red-500 to-pink-500 rounded-full border-2 border-white"></span>
                )}
              </button>
            }
            align="right"
            darkMode={darkMode}
          >
            <div
              className={`md:w-9 rounded-xl shadow-2xl border p-2 ${darkMode
                ? "bg-[#232336] border-white/10"
                : "bg-white border-gray-200"
                }`}
            >
              {/* Header */}
              <div className="flex items-center justify-between mb-4 pl-3">
                <div className="flex items-center gap-2">
                  <h3
                    className={`text-lg font-bold ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    Notifications
                  </h3>
                </div>
                {notifications.length > 0 && (
                  <span className="px-2 py-1 bg-green-600/20 text-green-600 text-xs rounded-full font-medium">
                    {notifications.length} new
                  </span>
                )}
              </div>
              {/* Notifications List */}
              <div className="max-h-64 overflow-y-auto scrollbar-hide">
                {
                  // loading ? (
                  //   <div className="text-center py-8">
                  //     <p className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"}`}>
                  //       Loading notifications...
                  //     </p>
                  //   </div>
                  // ) : 
                  notifications.length === 0 ? (
                    <div className="text-center py-8">
                      <div
                        className={`${darkMode ? "bg-white/5" : "bg-gray-100"
                          } w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full`}
                      >
                        <Bell
                          size={20}
                          className={`${darkMode ? "text-white/40" : "text-gray-400"
                            }`}
                        />
                      </div>
                      <p
                        className={`text-sm ${darkMode ? "text-white/60" : "text-gray-600"
                          }`}
                      >
                        No notifications
                      </p>
                      <p
                        className={`text-xs mt-1 ${darkMode ? "text-white/40" : "text-gray-500"
                          }`}
                      >
                        You're all caught up!
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {notifications.slice(0, 4).map((n) => (
                        renderNotification(n)
                      ))}
                    </div>
                  )}
              </div>
              {/* Footer */}
              <div
                className={`mt-4 pt-3 border-t ${darkMode ? "border-white/10" : "border-gray-200"}`}
              >
                <button
                  className="w-full text-center text-sm font-medium py-2 px-4 rounded-lg bg-gradient-to-r from-red-600 to-pink-600 hover:scale-105 text-white transition-all duration-200 shadow-lg"
                  onClick={() => setShowNotificationsModal(true)}
                >
                  View all notifications
                </button>
              </div>

            </div>
          </Dropdown>
           {showNotificationsModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
            <div className="bg-[#232336] rounded-2xl shadow-2xl p-8 md:p-12 w-[90vw] max-w-4xl relative max-h-[90vh] overflow-y-auto scrollbar-hide">

              {/* Close Button */}
              <button
                className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors duration-200"
                onClick={() => setShowNotificationsModal(false)}
              >
                <X size={28} />
              </button>

              {/* Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-red-600 to-pink-600">
                    <Bell size={20} className="text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-white">Notifications</h2>
                </div>
                <p className="text-white/60 text-lg">
                  Manage your notifications and stay updated
                </p>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 mb-8 bg-white/5 rounded-lg p-1 w-full">
                <button
                  className={` flex-1 px-6 py-3 rounded-md text-sm font-medium transition ${notificationsTab === "current"
                    ? "bg-white/10 text-white"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                    }`}
                  onClick={() => setNotificationsTab("current")}
                >
                  Current ({notifications.length})
                </button>

                <button
                  className={`flex-1 px-6 py-3 rounded-md text-sm font-medium transition ${notificationsTab === "cleared"
                    ? "bg-white/10 text-white"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                    }`}
                  onClick={() => setNotificationsTab("cleared")}
                >
                  Cleared ({clearedNotifications.length})
                </button>
              </div>

              {/* CURRENT TAB */}
              {notificationsTab === "current" && (
                <div className="space-y-6">
                  {notifications.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-white/5">
                        <Bell size={32} className="text-white/40" />
                      </div>
                      <h3 className="text-xl font-semibold text-white mb-2">
                        No notifications
                      </h3>
                      <p className="text-white/60">You're all caught up!</p>
                    </div>
                  ) : (
                    <>
                      <div className="grid gap-4">
                        {notifications.map((n) => (
                          <div
                            key={n.id}
                            className="group bg-white/5 rounded-xl p-6 border border-white/10 hover:border-white/20 transition-all duration-200"
                          >
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0 mt-1">{getNotificationIcon(n)}</div>

                              <div className="flex-1 min-w-0">
                                <p className="text-white/90 text-base leading-relaxed mb-2">
                                  {n.title}                        </p>

                                <div className="flex items-center gap-4 text-white/40 text-sm">
                                  <span>{new Date(n.createdAt).toLocaleString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}</span>

                                  {/* <span>•</span>

                                  <span>Unread</span> */}
                                </div>

                              </div>

                              {/* Delete Icon EXACT like UI 1 */}
                              <button
                                className="flex-shrink-0 p-2 rounded-lg hover:bg-red-600/20 text-white/60 hover:text-red-400 transition-all duration-200 group-hover:opacity-100 opacity-0"
                                onClick={() => handleClearNotification(n.id)}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Footer */}
                      <div className="flex gap-4 pt-6 border-t border-white/10">
                        <button
                          className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl transition-all duration-200"
                          onClick={() => setShowNotificationsModal(false)}
                        >
                          Close
                        </button>

                        <button
                          className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:scale-105 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={handleClearAll}
                          disabled={notifications.length === 0}
                        >
                          Clear All Notifications
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* CLEARED TAB */}
              {notificationsTab === "cleared" && (
  <div className="space-y-6">
    {clearedNotifications.length === 0 ? (
      <div className="text-center py-16">
        <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-white/5">
          <Trash2 size={32} className="text-white/40" />
        </div>
        <h3 className="text-xl font-semibold text-white mb-2">
          No cleared notifications
        </h3>
        <p className="text-white/60">
          Notifications you clear will appear here.
        </p>
      </div>
    ) : (
      <>
        <div className="grid gap-4">
          {clearedNotifications.map((n) => (
            <div
              key={n.id}
              className="group bg-white/5 rounded-xl p-6 border border-white/10 opacity-60 hover:opacity-100 transition-opacity duration-200"
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 mt-1">{getNotificationIcon(n)}</div>

                <div className="flex-1 min-w-0">
                  <p className="text-white/70 text-base leading-relaxed mb-2 line-through">
                    {n.title}
                  </p>

                  <div className="flex items-center gap-4 text-white/30 text-sm">
                    <span>Cleared</span>
                    <span>•</span>
                    <span>
                      {new Date(n.createdAt).toLocaleString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                </div>

                {/* Restore Button */}
                <button
                  className="flex-shrink-0 p-2 rounded-lg hover:bg-red-600/20 text-white/60 hover:text-red-400 transition-all duration-200 group-hover:opacity-100 opacity-0"
                  onClick={() => handleRestoreNotification(n.id)}
                  title="Restore notification"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        {/* <div className="flex gap-4 pt-6 border-t border-white/10">
          <button
            className="px-6 py-3 bg-white/10 hover:bg-white/20 border border-white/20 text-white rounded-xl transition-all duration-200"
            onClick={() => setShowNotificationsModal(false)}
          >
            Close
          </button>

          <button
            className="flex-1 bg-gradient-to-r from-red-600 to-pink-600 hover:scale-105 text-white font-semibold py-3 rounded-xl transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleRestoreAll}
            disabled={clearedNotifications.length === 0}
          >
            Restore All Notifications
          </button>
        </div> */}
      </>
    )}
  </div>
)}
            </div>
          </div>
        )}

          <Dropdown
            trigger={
              <div
                className={`flex items-center gap-3  rounded-xl cursor-pointer transition-all duration-200 ${darkMode ? "hover:bg-white/10" : "hover:bg-gray-100"
                  }`}
              >
                <img
                  src={user?.profile_picture ? `${process.env.REACT_APP_BACKEND_URL}${user?.profile_picture}` : "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"}
                  alt="profile"
                  className="w-9 h-9 rounded-full border-2 border-white/20"
                />
                <div className="hidden md:block text-left">
                  <div
                    className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"
                      }`}
                  >
                    {user ? user.first_name + " " + user.last_name : "Guest User"}
                  </div>
                  <div
                    className={`text-xs ${darkMode ? "text-white/60" : "text-gray-500"
                      }`}
                  >
                    Online
                  </div>
                </div>
                <svg
                  className={`w-4 h-4 ${darkMode ? "text-white/40" : "text-gray-400"
                    }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            }
            align="right"
            darkMode={darkMode}
          >
            <div className="w-full max-w-xs sm:max-w-sm">
              {/* Header */}
              <div className="p-4 sm:p-6 pb-3 sm:pb-4">
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="relative flex-shrink-0">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full p-0.5 bg-gradient-to-r from-pink-500 to-red-500">
                      <div className="w-full h-full rounded-full overflow-hidden">
                        <img
                          src={user?.profile_picture ? `${process.env.REACT_APP_BACKEND_URL}${user?.profile_picture}` : "https://cdn.pixabay.com/photo/2018/11/13/22/01/avatar-3814081_1280.png"}
                          alt="avatar"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`text-base sm:text-lg font-bold truncate ${darkMode ? "text-white" : "text-gray-900"
                        }`}
                    >
                      {user ? user.first_name + " " + user.last_name : "Guest User"}
                    </h3>
                    <p
                      className={`text-xs sm:text-sm truncate ${darkMode ? "text-white/60" : "text-gray-600"
                        }`}
                    >
                      {user ? user.email : "guest.user@email.com"}
                    </p>
                    <div
                      className={`text-xs sm:text-sm mt-1 ${darkMode ? "text-white/70" : "text-gray-600"
                        }`}
                    >
                      {user ? user.job : " "}
                    </div>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="px-2 sm:px-3 pb-3">
                <div className="space-y-0.5 sm:space-y-1">
                  <button
                    onClick={() => navigate("/settings")}
                    className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-left transition-all duration-200 group ${darkMode ? "hover:bg-white/10" : "hover:bg-gray-50"
                      }`}
                  >
                    <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                      <svg
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-xs sm:text-sm font-semibold truncate ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Settings
                      </div>
                      <div
                        className={`text-xs truncate ${darkMode ? "text-white/50" : "text-gray-500"
                          }`}
                      >
                        Preferences & privacy
                      </div>
                    </div>
                    <svg
                      className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${darkMode ? "text-white/30" : "text-gray-400"
                        } group-hover:scale-110 transition-transform duration-200`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>

                  <button
                    onClick={() => navigate("/help")}
                    className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-left transition-all duration-200 group ${darkMode ? "hover:bg-white/10" : "hover:bg-gray-50"
                      }`}
                  >
                    <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                      <svg
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div
                        className={`text-xs sm:text-sm font-semibold truncate ${darkMode ? "text-white" : "text-gray-900"
                          }`}
                      >
                        Help & Support
                      </div>
                      <div
                        className={`text-xs truncate ${darkMode ? "text-white/50" : "text-gray-500"
                          }`}
                      >
                        Get assistance
                      </div>
                    </div>
                    <svg
                      className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${darkMode ? "text-white/30" : "text-gray-400"
                        } group-hover:scale-110 transition-transform duration-200`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                </div>
                <div
                  className={`my-2 sm:my-3 mx-2 sm:mx-3 h-px ${darkMode
                    ? "bg-gradient-to-r from-transparent via-white/10 to-transparent"
                    : "bg-gradient-to-r from-transparent via-gray-200 to-transparent"
                    }`}
                ></div>
                <button
                  onClick={() => setShowLogoutModal(true)}
                  className={`w-full flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-2 sm:py-2.5 rounded-lg sm:rounded-xl text-left transition-all duration-200 group ${darkMode
                    ? "hover:bg-red-500/10 hover:text-red-400"
                    : "hover:bg-red-50 text-red-600"
                    }`}
                >
                  <div className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg sm:rounded-xl bg-gradient-to-br from-pink-600 to-red-500 shadow-md group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <svg
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                      />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs sm:text-sm font-semibold truncate">
                      <button>
                        {user ? "Sign Out" : "Login"}
                      </button>
                    </div>
                    <div
                      className={`text-xs truncate ${darkMode ? "text-white/50" : "text-gray-500"
                        }`}
                    >
                      Log out of your account
                    </div>
                  </div>
                  <svg
                    className={`w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 ${darkMode ? "text-white/30" : "text-gray-400"
                      } group-hover:scale-110 transition-transform duration-200`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
              {showLogoutModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#232336] backdrop-blur-sm rounded-2xl">
                  <div className="bg-[#232336] w-[90vw] max-w-md rounded-2xl p-8 shadow-2xl relative">

                    {/* Close Button */}
                    <button
                      className="absolute top-4 right-4 text-white/60 hover:text-white transition"
                      onClick={() => setShowLogoutModal(false)}
                    >
                      <X size={26} />
                    </button>

                    {/* Icon */}
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center 
      bg-gradient-to-br from-red-600 to-pink-600 shadow-lg">
                      <LogOut className="text-white" size={28} />
                    </div>

                    {/* Title */}
                    <h2 className="text-2xl font-bold text-white text-center mb-2">
                      Sign Out?
                    </h2>

                    {/* Description */}
                    <p className="text-center text-white/60 mb-6">
                      Are you sure you want to log out from your account?
                    </p>

                    {/* Buttons */}
                    <div className="flex gap-4">
                      <button
                        className="flex-1 py-3 rounded-xl bg-white/10 text-white border border-white/20 
          hover:bg-white/20 transition"
                        onClick={() => setShowLogoutModal(false)}
                      >
                        Cancel
                      </button>

                      <button
                        className="flex-1 py-3 rounded-xl bg-gradient-to-r from-red-600 to-pink-600 text-white
          font-semibold shadow-lg hover:scale-105 transition"
                        onClick={handleLogout}
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </Dropdown>

        </div>
      </div>
    </div>
  );
};

export default NavBar;